import React from "react";
import { DisabledPrompt, useBCMSContext, BCMSRecord, useStateManager, BCMSStateManagerCRUD, useUIState } from "bcms-react";
import Classes, { Account, User } from "model";

import { CenterContentContainer, ContentHeaderView, PageContainer, PageContentView, TableColumnContainer, TableColumnView, TableItemContainer, TableItemTextView, TableItemView, TableView } from 'components/shared';
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { TableBody } from 'components/shared/views/TableView';
import { PrimaryButton, UICheckbox } from 'components/ui';

import "./styles.css";

/**
 * @function EnrollmentPage
 * @return {React.Component}
 * @exported true
 * @description The main component for EnrollmentPage
 */
export default function EnrollmentPage(props){
    const globalController = useBCMSContext();
    BCMSRecord.globalControllerFor(Classes, globalController);

    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(), ...props.ui ?? {} };

    const controller = (state, setState) => ({
        data: state.data,
        ui: state.ui,

        onSelectAll: evt => {
            const updatedState = Account.initAll().reduce((result, account) => {
                return BCMSStateManagerCRUD.init(result)
                    .update([ "ui", `StatementItem_${ account.id }`, "isActive" ], true)
                    .submit();
            }, state);

            setState({ ...updatedState });
        },

        onChange: (key, value) => {
            BCMSStateManagerCRUD.init(state)
                .update([ "ui", key, "isActive" ], value)
                .submit(setState);
        }
    });

    return (
        <PageContainer className="enrollment-page" state={ { data, ui } } controller={ controller }>
            <EnrollmentContent />
        </PageContainer>
    )
}

export const getDefaultPageData = () => ({});
export const getDefaultPageUI = () => ({});

function EnrollmentContent(props) {
    const { onSelectAll } = useStateManager();

    const statements = Account.initAll().map(account => {
        return <StatementItem key={ `Statement_${ account.id }` } account={ account } />
    }, []);

    return (
        <PageContentView>
            <CenterContentContainer>
                <ContentHeaderView text="Electronic Document Enrollment"/>
                {/* Page content goes here */}
                <p className="info-text">If you do not see an account listed below, it is not elligible for paperless delivery at this time.</p>
                <TableView className="enrollment-table">
                    <TableColumnContainer className="">
                        <TableColumnView className="text-left" text="Account Nickname" />
                        <TableColumnView className="text-left" text="Account Number" />
                        <TableColumnView className="text-left" text="Primary Account Owner Email" />
                        <TableColumnView className="text-left" text="e-Statement" />
                    </TableColumnContainer>
                    <TableBody>
                        { statements }
                    </TableBody>
                </TableView>
                
                <p data-point="MarkAll" className="mark-all-option" onClick={ onSelectAll }>Mark all accounts for Electronic Delivery</p>
                
                <FormButtonRow>
                    <DisabledPrompt>
                        <PrimaryButton data-point="Save" text="Save" />
                    </DisabledPrompt>
                </FormButtonRow>
            </CenterContentContainer>
        </PageContentView>
    );
}

/**
 * @function StatementItem
 * @param {STRING} id - The id of the account
 * @param {STRING} nickname - The nickname of the account
 * @param {STRING} number - The number of the account
 * @param {STRING} email - The email set in the user
 * @param {BOOLEAN} markAll - The state of the mark all link
 * @returns {React.Component}
 * @example
 * <StatementItem nickname="name" number="1234" email="test@email.com" markAll={ state } />
 */
function StatementItem(props) {
    const { state: model } = useBCMSContext();
    const { onChange } = useStateManager();

    const account = props.account ?? Account.firstInstanceOf();
    const user = User.init(model.user);

    const { config, key } = useUIState(`StatementItem_${ account.id }`, { isActive: false });
    const change = value => onChange(key, value);
  
    return (
      <TableItemContainer>
        <TableItemTextView text={ account.nickname } />
        <TableItemTextView text={ account.number } />
        <TableItemTextView text={ user.email.value } />
        <TableItemView>
          <UICheckbox value={ config.isActive } onChange={ change } dataPoint={ `Statement_Item_${ account.id }` } label="" />
        </TableItemView>
      </TableItemContainer>
    );
  }